import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getBadge, addBadge, updateBadge, clearCurrentBadge } from '../actions/badgeActions';
import BadgeForm from '../components/badges/BadgeForm';
import ContentManagementSidebar from '../components/content-management/ContentManagementSidebar';
import { isAdminRole } from '../utils/roleHelpers';

const BadgeEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentBadge, badgeLoading } = useSelector(state => state.badge);
  const { user } = useSelector(state => state.auth);

  const isViewMode = location.pathname.includes('/view');
  const isEditMode = location.pathname.includes('/edit');
  const isAdmin = isAdminRole(user?.role);

  useEffect(() => {
    if (id) dispatch(getBadge(id));
    return () => { dispatch(clearCurrentBadge()); };
  }, [dispatch, id]);

  const handleSubmit = async (badgeData) => {
    try {
      if (id) {
        await dispatch(updateBadge(id, badgeData));
      } else {
        await dispatch(addBadge(badgeData));
      }
      navigate('/content-management/badges');
    } catch (error) {
      console.error('Error saving badge:', error);
    }
  };

  const handleClose = () => navigate('/content-management/badges');

  const handleTabChange = (tabId) => {
    if (tabId === 'Badges') navigate('/content-management/badges');
    else navigate('/content-management', { state: { activeTab: tabId } });
  };

  if (badgeLoading && id) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
        <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
          <ContentManagementSidebar activeTab="Badges" onTabChange={handleTabChange} isAdmin={isAdmin} isInstructor={false} />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6">
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (id && !currentBadge && !badgeLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
        <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
          <ContentManagementSidebar activeTab="Badges" onTabChange={handleTabChange} isAdmin={isAdmin} isInstructor={false} />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6">
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-red-500 text-xl mb-4">Badge not found</div>
              <button onClick={handleClose} className="px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors">
                Return to Badges
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const mode = isViewMode ? 'view' : isEditMode ? 'edit' : 'new';

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
        <ContentManagementSidebar activeTab="Badges" onTabChange={handleTabChange} isAdmin={isAdmin} isInstructor={false} />
        <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto">
          <BadgeForm badge={currentBadge} mode={mode} onSubmit={handleSubmit} onClose={handleClose} />
        </div>
      </div>
    </div>
  );
};

export default BadgeEditor;
